import { useRef } from "react";
import { Award, Users, Bot, FileText, Layers } from "lucide-react";
import { Reveal, Tag, H2 } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import { ChromaGrid, ChromaCard } from "@/components/ChromaGrid/ChromaGrid";
import FoldText from "@/components/FoldText/FoldText";

const diffs = [
  { icon: Award, title: "Built From Insurance Experience", copy: "Created by an insurance professional with over 30 years of industry experience.", span: "lg:col-span-6" },
  { icon: Users, title: "Designed For The Policyholder", copy: "Inssux is built for everyday people who feel confused, ignored, delayed, denied, or underpaid.", span: "lg:col-span-6" },
  { icon: Bot, title: "Powered By Jack", copy: "Jack turns insurance confusion into plain-English guidance.", span: "lg:col-span-4" },
  { icon: FileText, title: "Built To Capture The Real Story", copy: "Through Ask Jack, the Rip-Off Detector, and claim reporting, Inssux helps people document what happened and understand what may come next.", span: "lg:col-span-4" },
  { icon: Layers, title: "Expanding Across Claim Types", copy: "Starting with strong emphasis on property, disaster, homeowners, auto, and business claims, with broader support across major insurance categories.", span: "lg:col-span-4" },
];

const categories = [
  { title: "Homeowners Claims", copy: "Roof damage, water damage, storm damage, fire damage, and confusing property claim outcomes.", img: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=900&q=60", span: "lg:col-span-6", priority: true, accent: "#FFD166" },
  { title: "Disaster Claims", copy: "Hurricanes, wind, flooding-related disputes, storm losses, and large property damage situations.", img: "https://images.unsplash.com/photo-1544257750-572358f5da22?auto=format&fit=crop&w=900&q=60", span: "lg:col-span-6", priority: true, accent: "#FFD166" },
  { title: "Auto Claims", copy: "Denied, delayed, or underpaid auto-related claims.", img: "https://images.unsplash.com/photo-1713623311317-d3c43a4be4cf?auto=format&fit=crop&w=900&q=60", span: "lg:col-span-4", accent: "#8A2BE2" },
  { title: "Business Claims", copy: "Business owners trying to understand policy language, damage claims, or coverage disputes.", img: "https://images.unsplash.com/photo-1498811008858-d95a730b2ffc?auto=format&fit=crop&w=900&q=60", span: "lg:col-span-4", accent: "#8A2BE2" },
  { title: "Life & Health Claims", copy: "Support for people trying to understand confusing claim decisions and next-step options.", img: "https://images.pexels.com/photos/47327/medications-money-cure-tablets-47327.jpeg?auto=compress&cs=tinysrgb&w=900", span: "lg:col-span-4", accent: "#8A2BE2" },
];

export const Different = () => {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();

  return (
    <section id="different" data-testid="different-section" className="relative bg-[#121214] py-24 md:py-32">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="138, 43, 226"
        spotlightRadius={280}
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
        <Reveal>
          <Tag testId="different-tag">Why Inssux Is Different</Tag>
          <H2 className="max-w-4xl">
            <FoldText
              text="Not an insurance company. Not a law firm."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />{" "}
            <FoldText
              text="A guide built for the policyholder."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="#9D4EDD"
            />
          </H2>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
            Most insurance websites are built to sell policies. Most law firm websites are built to
            sign cases. Inssux is different. Inssux helps people understand what is happening before
            they make the next big decision.
          </p>
        </Reveal>
        <div ref={gridRef} className="bento-section mt-14 grid lg:grid-cols-12 gap-5">
          {diffs.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.07} className={d.span}>
              <ParticleCard
                testId={`diff-card-${i}`}
                disableAnimations={isMobile}
                glowColor="138, 43, 226"
                className="magic-bento-card magic-bento-card--border-glow h-full bg-[#0A0A0C] border border-white/10 p-7 hover:border-[#8A2BE2]/50 transition-colors duration-300"
              >
                <d.icon size={20} className="text-[#FFD166]" />
                <h3 className="mt-3 font-display text-base tracking-wide text-white">{d.title}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA] font-light leading-relaxed">{d.copy}</p>
              </ParticleCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Categories = () => {
  const isMobile = useMobileDetection();

  return (
    <section id="categories" data-testid="categories-section" className="bg-[#0A0A0C] py-24 md:py-32">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
        <Reveal>
          <Tag testId="categories-tag">Claim Categories</Tag>
          <H2 className="max-w-2xl">
            <FoldText
              text="Built for the claims that leave people feeling stuck."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />
          </H2>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
            Inssux is designed to support policyholders across multiple claim situations, with a
            strong initial focus on property and disaster-related claims.
          </p>
        </Reveal>
        <ChromaGrid className="mt-14 grid lg:grid-cols-12 gap-5" radius={200} disableAnimations={isMobile}>
          {categories.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.07} className={c.span}>
              <ChromaCard testId={`category-card-${i}`} borderColor={c.accent} className="group h-64">
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-50 grayscale-[40%] group-hover:opacity-65 group-hover:scale-105 transition-[opacity,transform] duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9) 10%, rgba(0,0,0,0))" }} />
                {c.priority && (
                  <span className="absolute top-4 right-4 bg-[#FFD166] px-2.5 py-1 font-mono-file text-[9px] tracking-[0.2em] text-black">
                    PRIORITY
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-lg tracking-wide text-white">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-[#A1A1AA] font-light leading-relaxed">{c.copy}</p>
                </div>
              </ChromaCard>
            </Reveal>
          ))}
        </ChromaGrid>
        <Reveal delay={0.3}>
          <p className="mt-9 font-hand text-2xl text-[#FFD166]/80 rotate-[-1deg]">
            Property claims are currently the biggest opportunity, especially situations involving
            hurricanes, wind, water, fire, storm damage, and roof claims.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
